import React, { useEffect, useState, useRef } from "react";
import Layout from "@/components/Layout";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Check, ChevronRight, Send, Sparkles, X, Play } from "lucide-react";

export default function CoursePlayer() {
  const { id } = useParams();
  const [course, setCourse] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const c = await api.get(`/courses/${id}`);
        setCourse(c.data);
        const enrs = await api.get("/enrollments/mine");
        const my = enrs.data.find(e => e.course_id === id);
        if (!my) {
          // try enroll automatically (free courses)
          try {
            const r = await api.post("/enrollments", { course_id: id });
            setEnrollment(r.data);
          } catch {
            toast.error("You must enroll first");
          }
        } else {
          setEnrollment(my);
        }
        if (c.data.modules?.[0]?.lessons?.[0]) {
          setCurrentLesson(c.data.modules[0].lessons[0]);
        }
      } catch {
        // ignore — page still renders with empty data
      }
    })();
  }, [id]);

  const completed = new Set(enrollment?.completed_lessons || []);

  const markComplete = async () => {
    if (!enrollment || !currentLesson) return;
    try {
      const r = await api.post(`/enrollments/${enrollment.enrollment_id}/progress`, {
        lesson_id: currentLesson.id,
        completed: true,
      });
      setEnrollment(r.data);
      if (r.data.status === "completed") {
        toast.success("🏆 Course completed! Certificate issued.");
      } else {
        toast.success("Lesson complete");
      }
    } catch {
      toast.error("Could not save progress");
    }
  };

  if (!course) return <Layout><div className="px-10 py-20">Loading…</div></Layout>;

  return (
    <Layout>
      <div className="grid grid-cols-12 min-h-screen">
        {/* Left: content */}
        <div className="col-span-12 lg:col-span-8 bg-[#0A0A0A] text-white px-10 py-10 min-h-screen">
          <Link to={`/courses/${id}`} className="overline text-zinc-500 hover:text-white">← Course details</Link>
          <div className="overline mt-6 text-[#E63946]">{course.category}</div>
          <h1 className="editorial-h1 text-4xl mt-2 mb-2">{currentLesson?.title || course.title}</h1>
          <div className="overline text-zinc-500 mb-8">{currentLesson?.duration_min || 0} min · {currentLesson?.type}</div>

          {currentLesson?.video_url && (
            <div className="aspect-video bg-zinc-900 border border-zinc-800 mb-8 flex items-center justify-center">
              <iframe src={currentLesson.video_url} className="w-full h-full" allow="autoplay; encrypted-media" allowFullScreen title="lesson"/>
            </div>
          )}
          {!currentLesson?.video_url && currentLesson && (
            <div className="aspect-video bg-zinc-900 border border-zinc-800 mb-8 flex items-center justify-center">
              <Play size={48} strokeWidth={1} className="text-zinc-700" />
            </div>
          )}

          <div className="font-serif text-lg leading-relaxed text-zinc-200 max-w-2xl whitespace-pre-wrap" data-testid="lesson-content">
            {currentLesson?.content || "No content for this lesson yet."}
          </div>

          {currentLesson?.quiz?.questions?.length > 0 && (
            <QuizBlock
              key={currentLesson.id}
              lesson={currentLesson}
              enrollmentId={enrollment?.enrollment_id}
              previousScore={enrollment?.quiz_scores?.[currentLesson.id]}
              passingScore={course.passing_score ?? 80}
              onSubmitted={(score) => {
                setEnrollment(e => ({ ...e, quiz_scores: { ...(e?.quiz_scores || {}), [currentLesson.id]: score } }));
              }}
            />
          )}

          <div className="mt-12 flex items-center gap-4">
            <button
              onClick={markComplete}
              data-testid="mark-complete-button"
              disabled={completed.has(currentLesson?.id)}
              className={`px-6 py-3 font-display text-sm transition-colors ${completed.has(currentLesson?.id) ? "bg-zinc-800 text-zinc-500" : "bg-[#E63946] text-white hover:bg-[#D62828]"}`}
            >
              {completed.has(currentLesson?.id) ? "✓ Completed" : "Mark complete"}
            </button>
            <button onClick={() => setChatOpen(true)} data-testid="open-coach" className="px-6 py-3 border border-zinc-700 text-sm font-display hover:bg-zinc-900 flex items-center gap-2">
              <Sparkles size={14} strokeWidth={1.5}/> Ask AI Coach
            </button>
          </div>
        </div>

        {/* Right: outline */}
        <div className="col-span-12 lg:col-span-4 bg-[#F9F9F8] px-8 py-10 border-l border-[#E4E4E7]">
          <div className="overline mb-3">Course progress</div>
          <div className="h-1 bg-[#E4E4E7] mb-1"><div className="h-1 bg-[#0A0A0A]" style={{ width: `${enrollment?.progress_pct || 0}%` }} /></div>
          <div className="overline mb-6">{enrollment?.progress_pct || 0}%</div>

          <h2 className="font-serif text-3xl mb-6 tracking-tight">Curriculum</h2>
          <div className="space-y-5">
            {course.modules?.map((m, mi) => (
              <div key={m.id}>
                <div className="overline mb-2">Module {mi+1} · {m.title}</div>
                <ul className="space-y-1">
                  {m.lessons?.map(l => (
                    <li key={l.id}>
                      <button
                        onClick={() => setCurrentLesson(l)}
                        data-testid={`lesson-${l.id}`}
                        className={`w-full text-left flex items-center justify-between gap-2 px-3 py-2 text-sm transition-colors border-l-2 ${currentLesson?.id === l.id ? "border-[#E63946] bg-white" : "border-transparent hover:border-[#0A0A0A] hover:bg-white"}`}
                      >
                        <span className="flex items-center gap-2 flex-1">
                          {completed.has(l.id) ? <Check size={14} strokeWidth={2} className="text-[#166534]"/> : <ChevronRight size={14} strokeWidth={1.5} className="text-[#52525B]"/>}
                          <span className={completed.has(l.id) ? "line-through text-[#52525B]" : ""}>{l.title}</span>
                        </span>
                        <span className="overline">{l.duration_min}m</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {chatOpen && <AICoachPanel courseId={id} lessonId={currentLesson?.id} onClose={() => setChatOpen(false)} />}
    </Layout>
  );
}

function QuizBlock({ lesson, enrollmentId, previousScore, passingScore = 80, onSubmitted }) {
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(previousScore !== undefined);
  const [score, setScore] = useState(previousScore ?? null);
  const questions = lesson.quiz.questions || [];
  const passed = score !== null && score >= passingScore;

  const submit = async () => {
    let correct = 0;
    questions.forEach((q, i) => {
      if (answers[i] === q.correct) correct++;
    });
    const sc = Math.round(100 * correct / Math.max(questions.length, 1));
    setScore(sc);
    setSubmitted(true);
    if (enrollmentId) {
      try {
        await api.post(`/enrollments/${enrollmentId}/quiz`, { lesson_id: lesson.id, score: sc });
        onSubmitted?.(sc);
      } catch {
        // swallow — quiz still scored locally
      }
    }
  };

  const reset = () => { setAnswers({}); setSubmitted(false); setScore(null); };

  return (
    <div className="mt-10 max-w-2xl border border-zinc-800 bg-zinc-900/50 p-6" data-testid="quiz-block">
      <div className="flex items-center justify-between mb-3">
        <div className="overline text-[#E63946]">Quiz · {questions.length} questions</div>
        <div className="overline text-zinc-500">pass ≥ {passingScore}%</div>
      </div>
      {questions.map((q, qi) => (
        <div key={qi} className="mb-6">
          <div className="font-display text-base mb-3 text-white">{qi + 1}. {q.q}</div>
          <div className="space-y-2">
            {q.options.map((opt, oi) => {
              const picked = answers[qi] === oi;
              const isCorrect = submitted && oi === q.correct;
              const isWrong = submitted && picked && oi !== q.correct;
              return (
                <button
                  key={oi}
                  disabled={submitted}
                  onClick={() => setAnswers({ ...answers, [qi]: oi })}
                  data-testid={`quiz-q${qi}-opt${oi}`}
                  className={`w-full text-left px-4 py-2.5 text-sm border transition-colors ${
                    isCorrect ? "border-[#166534] bg-[#166534]/20 text-white" :
                    isWrong ? "border-[#991B1B] bg-[#991B1B]/20 text-white" :
                    picked ? "border-[#E63946] bg-[#E63946]/10 text-white" :
                    "border-zinc-700 text-zinc-300 hover:border-zinc-500"
                  }`}
                >
                  {opt}
                </button>
              );
            })}
          </div>
          {submitted && q.explanation && (
            <div className="mt-2 text-xs text-zinc-500 italic">→ {q.explanation}</div>
          )}
        </div>
      ))}
      {!submitted ? (
        <button onClick={submit} disabled={Object.keys(answers).length < questions.length} data-testid="quiz-submit" className="px-6 py-3 bg-[#E63946] text-white text-sm font-display hover:bg-[#D62828] disabled:bg-zinc-800 disabled:text-zinc-500">
          Submit quiz
        </button>
      ) : (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div>
              <div className="overline text-[#E63946] mb-1">Score</div>
              <div className="font-display text-3xl font-bold text-white" data-testid="quiz-score">{score}%</div>
            </div>
            <div className={`px-3 py-1.5 text-xs font-display uppercase tracking-wider border ${passed ? "border-[#166534] bg-[#166534]/20 text-[#86EFAC]" : "border-[#991B1B] bg-[#991B1B]/20 text-[#FCA5A5]"}`} data-testid="quiz-pass-fail">
              {passed ? "Pass" : "Fail"}
            </div>
          </div>
          <button onClick={reset} className="text-sm overline text-zinc-400 hover:text-white">Retake →</button>
        </div>
      )}
    </div>
  );
}

function AICoachPanel({ courseId, lessonId, onClose }) {
  const [messages, setMessages] = useState([{ role: "assistant", content: "Hey! I'm your Forge coach. Ask me anything about this lesson — I'll explain it in plain English, give you examples, or quiz you." }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const send = async (e) => {
    e?.preventDefault();
    if (!input.trim() || loading) return;
    const text = input.trim();
    setInput("");
    setMessages(m => [...m, { role: "user", content: text }]);
    setLoading(true);
    try {
      const r = await api.post("/ai/coach", { course_id: courseId, lesson_id: lessonId, message: text, session_id: sessionId });
      setSessionId(r.data.session_id);
      setMessages(m => [...m, { role: "assistant", content: r.data.answer }]);
    } catch {
      setMessages(m => [...m, { role: "assistant", content: "Sorry, I couldn't reach the coach right now. Try again in a moment." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-y-0 right-0 w-full md:w-[420px] bg-white border-l border-[#0A0A0A] z-50 flex flex-col shadow-[-12px_0_40px_rgba(0,0,0,0.1)]" data-testid="ai-coach-panel">
      <div className="flex items-center justify-between px-6 py-5 border-b border-[#E4E4E7]">
        <div>
          <div className="overline text-[#E63946]">AI Coach</div>
          <div className="font-serif text-2xl">Forge Coach</div>
        </div>
        <button onClick={onClose} data-testid="close-coach" className="p-2 hover:bg-[#F9F9F8]"><X size={16}/></button>
      </div>
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : ""}`}>
            <div className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed ${m.role === "user" ? "bg-[#0A0A0A] text-white" : "bg-[#F9F9F8] border border-[#E4E4E7]"}`}>
              {m.content}
            </div>
          </div>
        ))}
        {loading && <div className="text-sm text-[#52525B]">Thinking…</div>}
        <div ref={bottomRef} />
      </div>
      <form onSubmit={send} className="px-6 py-4 border-t border-[#E4E4E7] flex gap-2">
        <input data-testid="coach-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask anything…" className="flex-1 px-3 py-2 border border-[#E4E4E7] bg-white focus:border-[#0A0A0A] outline-none text-sm" />
        <button type="submit" data-testid="coach-send" className="px-4 bg-[#E63946] text-white hover:bg-[#D62828]" disabled={loading}><Send size={14}/></button>
      </form>
    </div>
  );
}
